import type { Principal } from '@/lib/auth/principal';

/**
 * Tier-2 PIN session — held in sessionStorage so a page reload doesn't kick
 * the barista back to the lock screen. The token is short-lived (4h) and
 * already minted by identity-svc; we store it as-is alongside its expiry and
 * the Principal returned at login time.
 *
 * The storage key is intentionally namespaced under `qargo.pin.` so it can't
 * collide with MSAL's own sessionStorage state.
 */

const KEY = 'qargo.pin.session';

export interface PinSession {
  token: string;
  expiresAt: string; // ISO
  mustChangePin: boolean;
  principal: Principal;
}

export function loadPinSession(): PinSession | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PinSession;
    if (new Date(parsed.expiresAt).getTime() <= Date.now()) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function savePinSession(session: PinSession): void {
  sessionStorage.setItem(KEY, JSON.stringify(session));
}

export function clearPinSession(): void {
  sessionStorage.removeItem(KEY);
}
